import { av, THREE } from '@adsk/lmv';

export const RollTool2DName = 'rollTool2D';

const WorldUpToolName = 'worldup';
const WorldUpOverlayName = 'roll';
const RollActionName = 'roll';

// The right mouse button index.
const kMouseRight = 2;

// A wrapper around LMV's WorldUpTool to enable rotation around the 'up' axis (roll) via right-mouse-drag
// in a locked-to-top-down 2D view.
//
// WorldUpTool itself is only ever activated by a two-finger touch gesture (HotGestureTool) — nothing in LMV
// binds it to a mouse button, hence this wrapper.
//
// WorldUpTool implements roll by repeatedly reassigning Navigation's world-up vector among the axes
// perpendicular to the current view direction, snapping near canonical angles (WorldUpTool.js's
// RollInteraction/applyRoll). Looking straight down (as we always are, in this 2D top-down mode) excludes
// the model's real vertical axis from the snap candidates, so rolling continuously reassigns world-up to
// one of the *horizontal* axes instead.
//
// Every such change dirties the camera and fires a 'cameraChanged' event. Viewer3D registers its own
// listener for this (at viewer construction, in createControls()) that calls autocam.sync(), which detects
// the new world-up and calls Autocam.setWorldUpVector() to mirror it into Autocam's own cached
// sceneUpDirection/sceneFrontDirection/cubeFront. But that recompute derives sceneFrontDirection via a
// generic "pick a perpendicular axis" formula (Autocam.getWorldFrontVector/getWorldRightVector) that has
// no relationship to the model's actual front — it's designed for discrete "switch the model's up
// convention" cases, not for tracking a continuously-rolling camera.
//
// The ViewCube's rendered face, its isFaceView()-gated compass/arrows, and its click-to-face math all read
// these Autocam-cached fields (ViewCube.js's changeBasisWorldToStandard/isFaceView/getOrientation) — never
// the live camera directly. So once Autocam's copies are corrupted, the cube shows the wrong face and loses
// its compass/arrows/clickability, even though the actual camera (position, target, camera.up) stays
// exactly correct throughout the roll.
//
// The fix: pin Autocam's cached fields back to fixed, correct values every time this corruption can occur
// (see #restoreCubeOrientation/#cameraChanged), and reset Navigation's own world-up back to the same fixed
// axis once rolling ends (see #handleButtonUp) so that later, unrelated camera changes (e.g. panning) don't
// re-trigger autocam.sync()'s comparison against a stale rolled value and corrupt things again.
export class RollTool2D extends av.ToolInterface {
	#viewer;
	#rolling = false;
	// Default values. Set to the correct values via setUpAndFrontVectors.
	#fixedSceneUp = new THREE.Vector3(0, 0, 1);
	#fixedSceneFront = new THREE.Vector3(0, 1, 0);

	constructor(viewer) {
		super();
		this.#viewer = viewer;
		this.names = [RollTool2DName];
		// This is required because the super class is not an ES5 class, but an old-school constructor function that
		// sets these methods via this.<memberFunction> = <function>, which shadows our class members.
		this.getPriority = this.#getPriority.bind(this);
		this.handleButtonDown = this.#handleButtonDown.bind(this);
		this.handleButtonUp = this.#handleButtonUp.bind(this);
		this.handleMouseMove = this.#handleMouseMove.bind(this);
		this.handleBlur = this.#handleBlur.bind(this);

		this.onCameraChanged = this.#cameraChanged.bind(this);

		// Create and register the WorldUpTool, if it hasn't been registered already.
		if (!viewer.toolController.getTool(WorldUpToolName)) {
			viewer.toolController.registerTool(new av.WorldUpTool(viewer.impl, viewer));
		}
		// A slightly hacky (yet probably the best) way to hide the HUD created by the WorldUpTool.
		viewer.impl.removeOverlayScene(WorldUpOverlayName);
	}

	// ----- ToolInterface overrides -----
	// We declare all ToolInterface methods as private and bind them publicly in the constructor (see above).

	#getPriority() {
		// Make sure that our tool takes precendence over pan/worldup/gestures (all priority 0).
		// This way, we get to process events first and can call into the WorldUpTool as required.
		return 1;
	}

	#handleButtonDown(event, button) {
		if (button === kMouseRight && this.#viewer.navigation.isActionEnabled(RollActionName)) {
			this.#rolling = true;

			// Register a camera change handler to fix the view cube on every camera update while in 'roll' mode.
			this.#viewer.addEventListener(av.CAMERA_CHANGE_EVENT, this.onCameraChanged);

			// Activate the WorldUpTool.
			this.#viewer.setActiveNavigationTool(WorldUpToolName);

			// Forward the button down event to the WorldUpTool.
			var worldUp = this.#viewer.toolController.getTool(WorldUpToolName);
			worldUp.handleButtonDown(event, button);

			// Tell the tool controller that we consumed this event (prevents further propagation into the pan tool).
			return true;
		}
		return false;
	}

	#handleButtonUp(event, button) {
		if (this.#rolling && button === kMouseRight) {
			// Forward the event to the WorldUpTool.
			this.#viewer.toolController.getTool(WorldUpToolName).handleButtonUp(event, button);
			this.#cleanUp();
			return true;
		}
		return false;
	}

	#handleMouseMove(event) {
		if (this.#rolling) {
			// Simply forward the event to the WorldUpTool and prevent further event propagation.
			this.#viewer.toolController.getTool(WorldUpToolName).handleMouseMove(event);
			return true;
		}
		return false;
	}

	// We listen to this because it gets triggered when the window looses focus (e.g. via alt-tab, popup,
	// browser dev tools, etc). This can prevent the buttonUp handler to be called and leave this tool in an
	// inconsistent state (the WorldUpTool does some state cleanup on this event).
	#handleBlur(event) {
		if (this.#rolling) {
			this.#viewer.toolController.getTool(WorldUpToolName).handleBlur?.(event);
			this.#cleanUp();
			return true;
		}
		return false;
	}

	// ----- ToolInterface overrides END -----

	// Set the actual scene up and front vectors to fix camera issues while in 'roll' mode.
	// Should be set by the caller when activating this tool. Can be read from viewer.autocam.sceneUp-/FrontDirection.
	// This accepts THREE.Vector3 or plain arrays.
	setUpAndFrontVectors(upVector, frontVector) {
		if (Array.isArray(upVector)) {
			upVector = new THREE.Vector3().fromArray(upVector);
		} else {
			upVector = upVector.clone();
		}

		if (Array.isArray(frontVector)) {
			frontVector = new THREE.Vector3().fromArray(frontVector);
		} else {
			frontVector = frontVector.clone();
		}

		this.#fixedSceneUp = upVector;
		this.#fixedSceneFront = frontVector;
	}

	#cleanUp() {
		// Deactivate the WorldUpTool.
		this.#viewer.setActiveNavigationTool(); // pop worldup back off, calls its deactivate()

		// Fix viewer.navigation's up vector in addition to the autocam, to make sure that they are in sync
		// after leaving the 'roll' mode.
		this.#viewer.navigation.setWorldUpVector(this.#fixedSceneUp, /*reorient*/ false, /*force*/ true);
		this.#restoreCubeOrientation();

		this.#viewer.removeEventListener(av.CAMERA_CHANGE_EVENT, this.onCameraChanged);
		this.#rolling = false;
	}

	// Rotating around the 'up' axis via the WorldUpTool does not properly update the autocam,
	// which is used by the view cube. We update it here to avoid a messed up view cube state
	// (e.g. showing the wrong face, missing compass, etc).
	#restoreCubeOrientation() {
		const cam = this.#viewer.autocam;
		cam.sceneUpDirection.copy(this.#fixedSceneUp);
		cam.sceneFrontDirection.copy(this.#fixedSceneFront);
		cam.cubeFront.copy(this.#fixedSceneFront).cross(this.#fixedSceneUp).normalize();
		if (cam.cube) requestAnimationFrame(cam.cube.render);
	}

	// Fixes the autocam on camera update. See #restoreCubeOrientation and the comment on the event handler.
	// Must be a 'cameraChanged' listener, not tool.update()/handleMouseMove-driven: the corrupting autocam.sync()
	// call is dispatched from RenderCommandSystem as part of the render pipeline itself, decoupled from
	// ToolController.update() and from mouse events. Hooking 'cameraChanged' directly guarantees we run synchronously
	// right after the corruption, within the same dispatch, before the next paint.
	#cameraChanged() {
		if (this.#rolling) {
			this.#restoreCubeOrientation();
		}
		return false;
	}
}
